import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useRouteLoaderData } from 'react-router-dom'

import { LANGUAGE_LIST, nameFor, slugFor, type Lang } from '@glocal/lang'
import type { ILang, IWebsiteMode } from '@glocal/schemas'

import { track } from '../analytics'
import { trpc } from '../trpc'
import { checkUrl, type UrlCheck } from '../url'

import type { loader as rootLoader } from './root'
import { AddressStyleStep } from './wizard/address-style'
import { FetchOriginStep } from './wizard/fetch-origin'
import { UNKNOWN_DETECT, type Detect } from './wizard/types'

// The site-address helpers moved to ../url; re-exported so existing imports
// from this module keep working.
export { checkUrl, normalizeUrl, type UrlCheck } from '../url'

/**
 * Create the website plus the right shape of routes for the chosen mode.
 *
 *   - "subdomain": one custom hostname per target language using the
 *     `${lang}.${site-host}` convention. Works for customers whose DNS
 *     provider does not support apex CNAME (most local registrars).
 *
 *   - "subfolder": a single custom hostname at the apex (or www). The worker
 *     determines the language from the URL path, so we don't enumerate
 *     per-lang routes. Requires the customer's DNS provider to support apex
 *     CNAME flattening (Cloudflare, AWS Route53, etc.) — and, since that apex
 *     then points at us, a separate address to fetch the site from.
 *
 * Custom hostname registration may fail (rate limit, quota, DNS not yet set
 * up by the customer) — we don't abort the whole flow because the website is
 * still useful; the user can retry from the site detail page.
 */
async function createWebsiteWithRoutes(input: {
	accountId: string
	/** The address visitors use — stored as `website.public`. */
	siteOrigin: string
	/**
	 * Folder mode: where we fetch pages from once the site's own domain points
	 * at us. Stored as `website.origin`, which is what the proxy always fetches.
	 * Absent, we fetch from the site's own address.
	 */
	fetchOrigin?: string
	sourceLang: ILang
	targetLangs: ILang[]
	mode: IWebsiteMode
}) {
	const originHost = new URL(input.siteOrigin).hostname.replace(/^www\./, '')
	const slug = originHost.replace(/\W+/g, '-').toLowerCase()
	const name = slug.length >= 4 ? slug : slug.padEnd(4, '_')

	// In subfolder mode the customer's apex serves every language, so
	// `available_languages` must include the source too — the worker uses it
	// for hreflang generation and for recognising `/<source>/` as the original.
	const availableLanguages =
		input.mode === 'subfolder' ? Array.from(new Set([input.sourceLang, ...input.targetLangs])) : input.targetLangs

	const website = await trpc().WebsiteService.Create.mutate({
		account_id: input.accountId,
		name,
		lang: input.sourceLang,
		origin: input.fetchOrigin ?? input.siteOrigin,
		public: input.siteOrigin,
		active: true,
		banner: false,
		mode: input.mode,
		excluded_paths: [],
		excluded_languages: [],
		available_languages: availableLanguages,
	})

	// Demo preview per language on our own *.nayda.pro (no custom hostname
	// needed): the site page's language cards link here as "посмотреть превью".
	// Best-effort — a failed demo route never blocks onboarding.
	for (const lang of input.targetLangs) {
		try {
			await trpc().RouteService.Create.mutate({
				website_id: website.id,
				hostname: `${slugFor(lang)}-${slug}.nayda.pro`,
				lang,
			})
		} catch (e) {
			console.warn(`[Wizard] Failed to create demo route for ${lang}:`, e)
		}
	}

	if (input.mode === 'subfolder') {
		// One custom hostname covers the whole site — the worker reads /<lang>/
		// from the path. The apex (originHost) is what the customer CNAMEs to us.
		try {
			const customHostnameId = await trpc().CloudflareService.CreateCustomHostname.mutate({
				website_id: website.id,
				hostname: originHost,
			})
			await trpc().RouteService.Create.mutate({
				website_id: website.id,
				hostname: originHost,
				custom_hostname_id: customHostnameId,
			})
		} catch (e) {
			console.warn(`[Wizard] Failed to register apex ${originHost}:`, e)
		}
		return website
	}

	// Subdomain mode: register a REAL custom hostname only for the first
	// language — the free plan connects one language to the domain; the rest
	// stay demo-only until upgrade (decision 27.07). Site page enforces the
	// same rule for languages added later.
	for (const lang of input.targetLangs.slice(0, 1)) {
		// Slug (lowercase), not the raw code — `zh-Hant` would make a bad host.
		const hostname = `${slugFor(lang)}.${originHost}`
		try {
			const customHostnameId = await trpc().CloudflareService.CreateCustomHostname.mutate({
				website_id: website.id,
				hostname,
			})
			await trpc().RouteService.Create.mutate({
				website_id: website.id,
				hostname,
				lang,
				custom_hostname_id: customHostnameId,
			})
		} catch (e) {
			console.warn(`[Wizard] Failed to register ${hostname}:`, e)
		}
	}

	return website
}

/**
 * How long the address-style step waits for the DNS/hosting check. Past this
 * it shows both styles as "couldn't check" — a slow or unreachable API must
 * never leave the customer stuck on a spinner.
 */
const DETECT_TIMEOUT_MS = 8000

type Step = 'site' | 'languages' | 'style' | 'fetch-origin'

const STEP_NUMBER: Record<Step, number> = { site: 1, languages: 2, style: 3, 'fetch-origin': 3 }

export function WebsiteWizard() {
	const { account } = useRouteLoaderData('root') as Awaited<ReturnType<typeof rootLoader>>
	const navigate = useNavigate()
	const [step, setStep] = useState<Step>('site')
	const [origin, setOrigin] = useState('')
	const [sourceLang, setSourceLang] = useState<ILang>('en' as ILang)
	const [targetLangs, setTargetLangs] = useState<ILang[]>([])
	// The capability check fires in the background the moment the address is
	// confirmed, so by the address-style step the answer is usually in. `null`
	// while it runs; an error or a timeout lands as UNKNOWN_DETECT.
	const [detect, setDetect] = useState<Detect | null>(null)
	const detectRun = useRef<Promise<Detect> | null>(null)
	const [mode, setMode] = useState<IWebsiteMode | undefined>(undefined)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)

	// Entry point of the onboarding funnel. Everything downstream is measured as
	// a share of this, so it has to fire once per visit to the wizard and not
	// once per render — hence the empty dependency list.
	useEffect(() => {
		track('wizard_started')
	}, [])

	const urlCheck = checkUrl(origin)
	const normalizedOrigin = urlCheck.ok ? urlCheck.origin : null
	// Field-level error: only when the user has typed something invalid.
	const urlFieldError = origin.trim() ? urlErrorMessage(urlCheck) : null
	const siteHost = normalizedOrigin ? new URL(normalizedOrigin).hostname.replace(/^www\./, '') : ''

	function confirmSite() {
		if (!normalizedOrigin) {
			return
		}
		setError(null)
		setDetect(null)
		setMode(undefined)
		const timeout = new Promise<Detect>((resolve) => {
			window.setTimeout(() => resolve(UNKNOWN_DETECT), DETECT_TIMEOUT_MS)
		})
		const run = Promise.race([
			trpc()
				.WizardService.Detect.query({ url: normalizedOrigin })
				.catch((): Detect => UNKNOWN_DETECT),
			timeout,
		])
		detectRun.current = run
		void run.then((result) => {
			// Going back and changing the address starts a new check — only the
			// newest one may land.
			if (detectRun.current === run) {
				setDetect(result)
			}
		})
		setStep('languages')
		// Funnel step 1 → 2. The address is recorded on purpose: when a connection
		// goes wrong we need to know WHICH site it was and what kind — the whole
		// flow branches on the customer's hosting and DNS. It stays inside our own
		// analytics project, alongside the rest of the account's data.
		track('wizard_site_confirmed', { origin: normalizedOrigin })
	}

	function confirmLanguages() {
		if (targetLangs.length === 0) {
			setError('Pick at least one target language.')
			return
		}
		setError(null)
		setStep('style')
		track('wizard_languages_picked', { target_language_count: targetLangs.length })
	}

	function chooseStyle(style: IWebsiteMode) {
		setMode(style)
		// Whether people follow the recommendation — and whether the check had
		// answered when they chose — is what tells us if this step earns its place.
		track('wizard_style_chosen', {
			mode: style,
			recommended_mode: detect?.recommended_mode,
			apex_cname: detect?.apex_cname,
			dns_provider: detect?.dns_provider,
			detect_resolved: detect !== null && detect !== UNKNOWN_DETECT,
		})
		if (style === 'subfolder') {
			setError(null)
			setStep('fetch-origin')
			return
		}
		void submit('subdomain')
	}

	async function submit(chosenMode: IWebsiteMode, fetchOrigin?: string) {
		if (!normalizedOrigin) {
			setError('Site address looks off — go back and check.')
			return
		}
		setSubmitting(true)
		setError(null)
		const capabilities = detect ?? UNKNOWN_DETECT
		try {
			const website = await createWebsiteWithRoutes({
				accountId: account.id,
				siteOrigin: normalizedOrigin,
				fetchOrigin,
				sourceLang,
				targetLangs,
				mode: chosenMode,
			})
			// The conversion this whole funnel exists to measure.
			track('website_created', {
				origin: normalizedOrigin,
				mode: chosenMode,
				recommended_mode: capabilities.recommended_mode,
				// A folder site fetched from its own separate address — the one
				// setup where the domain and the source differ.
				separate_fetch_origin: fetchOrigin !== undefined,
				target_language_count: targetLangs.length,
				source_language: sourceLang,
				// What the capability probe made of the site. These are the fields
				// that decide the whole connection path, so they are what a later
				// "why did this site end up like that?" question is asked against.
				hosting: capabilities.hosting,
				dns_provider: capabilities.dns_provider,
				apex_cname: capabilities.apex_cname,
				hidden_origin: capabilities.hidden_origin,
				detect_resolved: capabilities !== UNKNOWN_DETECT,
			})
			// Land the user on the site detail page directly — avoids the empty
			// dashboard caused by KV eventual consistency on the account-website
			// index, and is what they want to see anyway.
			navigate(`/site/${website.id}`)
		} catch (e) {
			// A drop-off here is our fault, not the user's — keep the reason so the
			// failures can be told apart from people who simply changed their mind.
			track('website_create_failed', {
				origin: normalizedOrigin,
				mode: chosenMode,
				reason: e instanceof Error ? e.message : 'unknown',
			})
			setError(e instanceof Error ? e.message : 'Failed to create website')
			setSubmitting(false)
		}
	}

	return (
		<div className="max-w-[640px] mx-auto">
			<div className="mb-8">
				<button
					onClick={() => navigate('/')}
					className="label-mono text-ink-soft hover:text-ink"
				>
					← Back to your sites
				</button>
			</div>

			<StepHeader step={STEP_NUMBER[step]} />

			{/* Top-level banner is for the later steps (server / submit errors);
			    step 1 shows its problems on the field itself. */}
			{step !== 'site' && error && (
				<div className="mb-4 text-[13px] text-[#B23B2A] bg-[#B23B2A]/10 rounded-nayda-sm px-4 py-3 border border-[#B23B2A]/20">
					{error}
				</div>
			)}

			{step === 'site' && (
				<SiteStep
					origin={origin}
					onOrigin={setOrigin}
					normalizedOrigin={normalizedOrigin}
					fieldError={urlFieldError}
					onContinue={confirmSite}
				/>
			)}

			{step === 'languages' && (
				<LanguagesStep
					sourceLang={sourceLang}
					onSourceLang={setSourceLang}
					targetLangs={targetLangs}
					onTargetLangs={setTargetLangs}
					onBack={() => setStep('site')}
					onContinue={confirmLanguages}
				/>
			)}

			{step === 'style' && (
				<AddressStyleStep
					host={siteHost}
					detect={detect}
					initialStyle={mode}
					submitting={submitting}
					onBack={() => setStep('languages')}
					onContinue={chooseStyle}
				/>
			)}

			{step === 'fetch-origin' && (
				<FetchOriginStep
					host={siteHost}
					siteOrigin={normalizedOrigin ?? ''}
					hosting={detect?.hosting ?? 'unknown'}
					submitting={submitting}
					onBack={() => setStep('style')}
					onUseSubdomains={() => {
						setMode('subdomain')
						setStep('style')
					}}
					onContinue={(fetchOrigin) => {
						track('wizard_fetch_origin_entered', { hosting: detect?.hosting ?? 'unknown' })
						void submit('subfolder', fetchOrigin)
					}}
				/>
			)}
		</div>
	)
}

function SiteStep({
	origin,
	onOrigin,
	normalizedOrigin,
	fieldError,
	onContinue,
}: {
	origin: string
	onOrigin: (value: string) => void
	normalizedOrigin: string | null
	fieldError: string | null
	onContinue: () => void
}) {
	return (
		<div className="card p-5 sm:p-8 flex flex-col gap-5">
			<div>
				<div className="label-mono mb-2">Step 1</div>
				<h2 className="font-sans text-[24px] sm:text-[28px] font-medium leading-tight text-ink">
					Where does your site live?
				</h2>
				<p className="text-ink-soft text-[14px] mt-1">We'll sniff out the strings. You pick the markets.</p>
			</div>

			<label className="flex flex-col gap-1.5">
				<span className="label-mono">Site address</span>
				<input
					type="text"
					required
					autoFocus
					inputMode="url"
					autoComplete="url"
					autoCapitalize="off"
					spellCheck={false}
					value={origin}
					onChange={(e) => onOrigin(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' && normalizedOrigin) {
							e.preventDefault()
							onContinue()
						}
					}}
					aria-invalid={!!fieldError}
					className={
						'field ' + (fieldError ? 'border-[#B23B2A] focus:border-[#B23B2A] focus:ring-[#B23B2A]/30' : '')
					}
					placeholder="example.com"
				/>
				{fieldError ? (
					<span className="text-[12px] text-[#B23B2A]">{fieldError}</span>
				) : (
					<span className="text-[11px] text-ink-mute font-mono">
						{normalizedOrigin ? `We'll proxy ${normalizedOrigin}` : 'https:// is added for you'}
					</span>
				)}
			</label>

			<button
				onClick={onContinue}
				disabled={!normalizedOrigin}
				className="btn btn-accent self-start mt-2"
			>
				Continue →
			</button>
		</div>
	)
}

function LanguagesStep({
	sourceLang,
	onSourceLang,
	targetLangs,
	onTargetLangs,
	onBack,
	onContinue,
}: {
	sourceLang: ILang
	onSourceLang: (lang: ILang) => void
	targetLangs: ILang[]
	onTargetLangs: (langs: ILang[]) => void
	onBack: () => void
	onContinue: () => void
}) {
	// Pick as many languages as you like — every pick gets a free demo preview
	// (decision 27.07). The FREE plan limit applies to CONNECTING languages to
	// the real domain (1 free, rest behind upgrade), enforced on the site page.
	const maxTargetLangs = 99

	return (
		<div className="card p-5 sm:p-8 flex flex-col gap-5">
			<div>
				<div className="label-mono mb-2">Step 2</div>
				<h2 className="font-sans text-[24px] sm:text-[28px] font-medium leading-tight text-ink">
					Which way are we crossing?
				</h2>
				<p className="text-ink-soft text-[14px] mt-1">
					Pick as many as you like — every language gets a free preview. Free plan connects 1 language to your
					domain; upgrade unlocks the rest.
				</p>
			</div>

			<label className="flex flex-col gap-1.5">
				<span className="label-mono">Source language</span>
				<LanguagePicker
					value={sourceLang}
					onChange={onSourceLang}
				/>
			</label>

			<label className="flex flex-col gap-1.5">
				<span className="label-mono">Translate into</span>
				<LanguageMultiPicker
					value={targetLangs}
					onChange={onTargetLangs}
					exclude={sourceLang}
					max={maxTargetLangs}
				/>
			</label>

			{/* How the language addresses look is the next step — by then the
			    background check has usually answered what this domain can do. */}

			<div className="flex gap-3 mt-2">
				<button
					onClick={onBack}
					className="btn btn-ghost"
				>
					← Back
				</button>
				<button
					onClick={onContinue}
					className="btn btn-accent"
				>
					Continue →
				</button>
			</div>
		</div>
	)
}

const STEP_LABELS = ['Site', 'Languages', 'Address'] as const

function StepHeader({ step }: { step: number }) {
	return (
		<div className="mb-6 flex items-center gap-3">
			{STEP_LABELS.map((label, i) => (
				<Fragment key={label}>
					{i > 0 && <div className="h-px flex-1 bg-line" />}
					<StepDot
						active={step >= i + 1}
						done={step > i + 1}
						num={i + 1}
						label={label}
					/>
				</Fragment>
			))}
		</div>
	)
}

function StepDot({ active, done, num, label }: { active: boolean; done: boolean; num: number; label: string }) {
	const current = active && !done
	return (
		<div className="flex items-center gap-2">
			<div
				className={
					'w-7 h-7 rounded-full flex items-center justify-center font-mono text-[12px] ' +
					(done ? 'bg-marigold text-ink' : active ? 'bg-ink text-paper' : 'bg-surface text-ink-mute')
				}
			>
				{done ? '✓' : num}
			</div>
			{/* Three labelled dots don't fit a phone — there only the current step keeps its label. */}
			<span className={'label-mono ' + (current ? '' : 'hidden sm:inline')}>{label}</span>
		</div>
	)
}

const COMMON_LANGS: ILang[] = ['en', 'ru', 'es', 'fr', 'de', 'it', 'pt', 'pl', 'tr', 'uk', 'zh', 'ja'] as ILang[]

function LanguagePicker({ value, onChange }: { value: ILang; onChange: (l: ILang) => void }) {
	const all = useMemo(() => {
		const list = (LANGUAGE_LIST as readonly string[]).map((l) => ({
			code: l as ILang,
			label: langLabel(l),
		}))
		list.sort((a, b) => a.label.localeCompare(b.label))
		return list
	}, [])
	return (
		<select
			value={value}
			onChange={(e) => onChange(e.target.value as ILang)}
			className="field"
		>
			{all.map((l) => (
				<option
					key={l.code}
					value={l.code}
				>
					{l.label} ({l.code.toUpperCase()})
				</option>
			))}
		</select>
	)
}

/**
 * Resolve `es` → `Spanish` etc. via our own language registry (the single
 * table), not the browser's Intl database — Intl labels both fil and tl as
 * "Filipino" (ambiguous) and we want control over edge cases. Falls back to the
 * bare code for anything not in the registry.
 */
function langLabel(code: string): string {
	return nameFor(code as Lang) ?? code.toUpperCase()
}

function LanguageMultiPicker({
	value,
	onChange,
	exclude,
	max,
}: {
	value: ILang[]
	onChange: (l: ILang[]) => void
	exclude?: ILang
	max: number
}) {
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const popoverRef = useRef<HTMLDivElement>(null)

	function toggle(l: ILang) {
		if (value.includes(l)) {
			onChange(value.filter((x) => x !== l))
		} else if (value.length < max) {
			onChange([...value, l])
			// On a single-pick plan, close the dropdown right after the pick.
			if (max === 1) {
				setOpen(false)
			}
		}
	}

	// Close popover on outside click. We don't trap focus here — the dropdown
	// is short-lived and esc-to-close handled via blur on input.
	useEffect(() => {
		if (!open) {
			return
		}
		function handleClick(e: MouseEvent) {
			if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClick)
		return () => document.removeEventListener('mousedown', handleClick)
	}, [open])

	// All langs, alphabetised by display name. Excludes the source language.
	const allLangs = useMemo(() => {
		const list = (LANGUAGE_LIST as readonly string[])
			.filter((l) => l !== exclude)
			.map((l) => ({ code: l as ILang, label: langLabel(l) }))
		list.sort((a, b) => a.label.localeCompare(b.label))
		return list
	}, [exclude])

	// Languages selected by the user that aren't in the common pills row — we
	// surface them as extra pills so the user always sees their choices.
	const extraSelected = value.filter((l) => !COMMON_LANGS.includes(l))

	const filtered = search
		? allLangs.filter(
				(l) =>
					l.label.toLowerCase().includes(search.toLowerCase()) ||
					l.code.toLowerCase().includes(search.toLowerCase())
			)
		: allLangs

	return (
		<div className="flex flex-col gap-2">
			{/* Common languages — quick picks */}
			<div className="flex flex-wrap gap-2">
				{COMMON_LANGS.filter((l) => l !== exclude).map((l) => (
					<LangPillButton
						key={l}
						code={l}
						selected={value.includes(l)}
						disabled={!value.includes(l) && value.length >= max}
						onClick={() => toggle(l)}
					/>
				))}
				{/* Extra picks (chose from the dropdown) appear inline. */}
				{extraSelected.map((l) => (
					<LangPillButton
						key={l}
						code={l}
						selected
						disabled={false}
						onClick={() => toggle(l)}
					/>
				))}
			</div>

			{/* Searchable popover for the long tail */}
			<div
				ref={popoverRef}
				className="relative"
			>
				<button
					type="button"
					onClick={() => setOpen((v) => !v)}
					className="font-mono text-[11px] tracking-[0.1em] text-ink-soft hover:text-ink transition-colors"
				>
					{open ? '× Close' : `+ More languages (${allLangs.length} total)`}
				</button>

				{open && (
					<div className="absolute z-20 mt-2 left-0 right-0 max-w-md card p-3 shadow-lg">
						<input
							type="text"
							autoFocus
							placeholder="Search…"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="field mb-2"
						/>
						<div className="max-h-[280px] overflow-y-auto -mx-1 px-1">
							{filtered.length === 0 ? (
								<div className="px-3 py-6 text-center text-ink-mute text-[13px]">
									No languages match "{search}"
								</div>
							) : (
								<ul className="flex flex-col">
									{filtered.map((l) => {
										const selected = value.includes(l.code)
										const disabled = !selected && value.length >= max
										return (
											<li key={l.code}>
												<button
													type="button"
													disabled={disabled}
													onClick={() => toggle(l.code)}
													className={
														'w-full flex items-center justify-between px-3 py-2 rounded-nayda-sm text-[14px] transition-colors text-left ' +
														(selected
															? 'bg-marigold/15 text-ink'
															: disabled
																? 'text-ink-mute cursor-not-allowed opacity-50'
																: 'hover:bg-surface text-ink')
													}
												>
													<span>{l.label}</span>
													<span className="font-mono text-[11px] text-ink-mute uppercase">
														{selected && '✓ '}
														{l.code}
													</span>
												</button>
											</li>
										)
									})}
								</ul>
							)}
						</div>
						{max < 99 && (
							<div className="mt-2 px-3 pt-2 border-t border-line text-[11px] text-ink-mute font-mono">
								{value.length} / {max} selected
								{value.length >= max && ' — remove one to swap'}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

function LangPillButton({
	code,
	selected,
	disabled,
	onClick,
}: {
	code: ILang
	selected: boolean
	disabled: boolean
	onClick: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			title={langLabel(code)}
			className={
				'font-mono text-[12px] uppercase tracking-[0.1em] px-3 py-2 rounded-nayda-sm border transition-colors ' +
				(selected
					? 'bg-marigold border-marigold text-ink'
					: disabled
						? 'bg-bone border-line text-ink-mute cursor-not-allowed opacity-50'
						: 'bg-paper border-line text-ink hover:border-line-strong')
			}
		>
			{code}
		</button>
	)
}

function urlErrorMessage(check: UrlCheck): string | null {
	if (check.ok) {
		return null
	}
	switch (check.reason) {
		case 'empty':
			return null // don't shout at an empty field
		case 'invalid-chars':
			return 'Only letters, digits, dots and hyphens are allowed.'
		case 'missing-tld':
			return "Don't forget the domain zone (.com, .ru, .io…)."
		case 'malformed':
			return 'Site address looks off — try something like example.com.'
	}
}
